import type {
    WeekdayIndex,
    WeeklyCheckinPreferences,
    WeeklyCheckinPreferencesRow,
} from '@/types/weekly-checkin';
import { DEFAULT_WEEKLY_CHECKIN_PREFERENCES } from '@/types/weekly-checkin';

const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

function normalizeWeekday(value: unknown, fallback: WeekdayIndex): WeekdayIndex {
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6) {
        return value as WeekdayIndex;
    }
    return fallback;
}

function normalizeTime(value: unknown, fallback: string): string {
    if (typeof value === 'string' && TIME_REGEX.test(value)) {
        return value;
    }
    return fallback;
}

function normalizeBoolean(value: unknown, fallback: boolean): boolean {
    if (typeof value === 'boolean') return value;
    return fallback;
}

export function normalizeWeeklyCheckinPreferences(
    value: unknown,
    fallback: WeeklyCheckinPreferences = DEFAULT_WEEKLY_CHECKIN_PREFERENCES
): WeeklyCheckinPreferences {
    if (!value || typeof value !== 'object') {
        return { ...fallback };
    }

    const raw = value as Record<string, unknown>;
    const dayOfWeekRaw = raw.dayOfWeek ?? raw.day_of_week;
    const pushEnabledRaw = raw.pushEnabled ?? raw.push_enabled;

    return {
        enabled: normalizeBoolean(raw.enabled, fallback.enabled),
        dayOfWeek: normalizeWeekday(dayOfWeekRaw, fallback.dayOfWeek),
        time: normalizeTime(raw.time, fallback.time),
        pushEnabled: normalizeBoolean(pushEnabledRaw, fallback.pushEnabled),
    };
}

export function toWeeklyCheckinPreferencesRow(
    preferences: WeeklyCheckinPreferences
): WeeklyCheckinPreferencesRow {
    return {
        enabled: preferences.enabled,
        day_of_week: preferences.dayOfWeek,
        time: preferences.time,
        push_enabled: preferences.pushEnabled,
    };
}

export function fromWeeklyCheckinPreferencesRow(
    row: WeeklyCheckinPreferencesRow | null | undefined,
    fallback: WeeklyCheckinPreferences = DEFAULT_WEEKLY_CHECKIN_PREFERENCES
): WeeklyCheckinPreferences {
    return normalizeWeeklyCheckinPreferences(row, fallback);
}
